import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Globe } from 'lucide-react';
import { PROFILES } from './profiles';
import { CARD } from './Shared';

// Carrusel horizontal: en móvil cada tarjeta queda centrada (snap-center), en desktop flechas y puntos.
export function ProfileCarousel() {
  const trackRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);
  const update = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 4);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
    const card = el.firstElementChild as HTMLElement | null;
    if (card) {
      const step = card.offsetWidth + 20;
      setActive(Math.min(PROFILES.length - 1, Math.round(el.scrollLeft / step)));
    }
  }, []);
  useEffect(() => {
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [update]);
  const scrollToIndex = (i: number) => {
    const el = trackRef.current;
    const card = el?.children[i] as HTMLElement | undefined;
    if (el && card) el.scrollTo({
      left: card.offsetLeft - el.offsetLeft - (el.clientWidth - card.offsetWidth) / 2,
      behavior: 'smooth'
    });
  };
  const page = (dir: 1 | -1) => {
    const el = trackRef.current;
    if (el) el.scrollBy({
      left: dir * el.clientWidth * 0.9,
      behavior: 'smooth'
    });
  };
  const arrow = 'inline-flex h-11 w-11 items-center justify-center rounded-full border border-gray-200 bg-white text-ink shadow-[0_6px_16px_-8px_rgba(11,13,18,0.3)] transition-[opacity,color,border-color] duration-150 ease-out hover:border-brand hover:text-brand disabled:opacity-30 disabled:pointer-events-none';
  return <div className="mt-12" role="region" aria-roledescription="carrusel" aria-label="Historias" data-id="element-626" data-mp="2eonfs84647tk">
      <div ref={trackRef} onScroll={update} className="-mx-5 sm:-mx-8 flex snap-x snap-mandatory gap-5 overflow-x-auto scroll-smooth px-[8vw] sm:px-8 pb-6 pt-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-id="element-627" data-mp="29pk9kkcgbvk0">
        {PROFILES.map((p, i) => <article key={p.name} aria-label={`${i + 1} de ${PROFILES.length}`} className={`${CARD} flex w-[84vw] max-w-[360px] shrink-0 snap-center sm:snap-start flex-col items-center p-6 text-center sm:w-[360px] sm:items-start sm:p-7 sm:text-left`} data-id="element-628" data-mp="1gddweiir6w38" data-mpi={i}>
            <div className="flex flex-col items-center gap-3 sm:flex-row sm:gap-3.5" data-id="element-629" data-mp="1v29z0021jgwg">
              <img src={p.photo} alt="" width={64} height={64} loading="lazy" decoding="async" className="h-16 w-16 sm:h-14 sm:w-14 shrink-0 rounded-full object-cover ring-2 ring-white shadow-[0_6px_16px_-8px_rgba(11,13,18,0.35)]" data-id="element-630" data-mp="1z6q4w1bnmobo" />
              <div className="text-center sm:text-left" data-id="element-631" data-mp="4ti7q307i0mv">
                <h3 className="font-bold text-[17px] text-ink leading-tight" data-id="element-632" data-mp="9rjmibqlndsj">{p.name}</h3>
                <p className="mt-0.5 flex items-center justify-center sm:justify-start gap-1.5 text-[13px] text-gray-500" data-id="element-633" data-mp="2791abt96okp"><Globe className="h-3.5 w-3.5" strokeWidth={2} aria-hidden="true" data-id="element-634" data-mp="3tklvw8owg8v1" />{p.place}</p>
              </div>
            </div>
            <h4 className="mt-5 font-bold text-[18px] leading-snug text-ink [text-wrap:balance]" data-id="element-635" data-mp="3omtr4e35os9f">“{p.title}”</h4>
            <p className="mt-2 text-[15px] leading-relaxed text-gray-600" data-id="element-636" data-mp="3jle9yx43n5wb">{p.body}</p>
          </article>)}
      </div>

      <div className="mt-2 flex items-center justify-center gap-5" data-id="element-637" data-mp="g70dc707luw2">
        <button type="button" onClick={() => page(-1)} disabled={!canPrev} className={arrow} aria-label="Perfiles anteriores" data-id="element-638" data-mp="g4nu7qiptehf">
          <ChevronLeft className="h-5 w-5" strokeWidth={2.2} data-id="element-639" data-mp="76c6pxd5awt7" />
        </button>
        <div className="flex items-center gap-2" data-id="element-640" data-mp="78eueaqtsdur">
          {PROFILES.map((p, i) => <button key={p.name} type="button" onClick={() => scrollToIndex(i)} aria-label={`Ver perfil ${i + 1}`} aria-current={i === active} className={`h-2 rounded-full transition-[width,background-color] duration-200 ease-out ${i === active ? 'w-6 bg-brand' : 'w-2 bg-gray-300 hover:bg-gray-400'}`} data-id="element-641" data-mp="2vvs98582wim7" data-mpi={i} />)}
        </div>
        <button type="button" onClick={() => page(1)} disabled={!canNext} className={arrow} aria-label="Más perfiles" data-id="element-642" data-mp="eo8ri10bw1fr">
          <ChevronRight className="h-5 w-5" strokeWidth={2.2} data-id="element-643" data-mp="12dtdksrs3et9" />
        </button>
      </div>
    </div>;
}